import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  RefreshCw, FileCheck, Tag, FileText, Play, Loader2, 
  CheckCircle, XCircle, SkipForward, Clock, ChevronDown, ChevronUp,
  History, AlertTriangle, Calculator, DollarSign
} from "lucide-react";
import { formatDateIST } from "@/utils/dateUtils";

// Order statuses
const ORDER_STATUSES = [
  "CREATED", "MANIFESTED", "PICKED_UP", "LEFT_ORIGIN", "REACH_DESTINATION",
  "UNDEL_REATTEMPT", "PART_DEL", "OFD", "DELIVERED", "RETURNED_INTRANSIT",
  "RECEIVED_AT_RETURN_CENTER", "RETURN_OFD", "RETURN_DELIVERED", "NOT_PICKED", "LOST", "CANCELLED"
];

const LABEL_SIZES = [
  { value: "SM", label: "Small (3\" x 2\")" },
  { value: "MD", label: "Medium (4\" x 2\")" },
  { value: "A4", label: "A4 Full Page" },
  { value: "STD", label: "Standard (4\" x 6\")" }
];

const OPERATION_ICONS = {
  UPDATE_STATUS: RefreshCw,
  FETCH_POD: FileCheck,
  FETCH_SHIPPING_LABEL: Tag,
  FETCH_LR_COPY: FileText,
  CALCULATE_SELLER_CHARGES: Calculator,
  BULK_FETCH_TRACKING: RefreshCw
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800"
};

const BulkOperations = ({ user, onLogout }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState("general"); // general or seller-charges
  
  // Filter state
  const [shippingPartners, setShippingPartners] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState("");
  const [selectedLabelSize, setSelectedLabelSize] = useState("");
  const [orderCount, setOrderCount] = useState(null);
  
  // Operations state
  const [availableOperations, setAvailableOperations] = useState([]);
  const [executing, setExecuting] = useState(false);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [pollingOperationId, setPollingOperationId] = useState(null);

  // Seller Charges Calculation state
  const [chargesSelectedSeller, setChargesSelectedSeller] = useState("");
  const [chargesSelectedPartner, setChargesSelectedPartner] = useState("");
  const [chargesStats, setChargesStats] = useState(null);
  const [chargesStatsLoading, setChargesStatsLoading] = useState(false);
  const [recalculateAll, setRecalculateAll] = useState(false);
  const [adjustmentMode, setAdjustmentMode] = useState("adjustment"); // "overwrite" or "adjustment"
  const [chargesExecuting, setChargesExecuting] = useState(false);

  // Bulk Fetch Tracking state
  const [trackingSelectedSeller, setTrackingSelectedSeller] = useState("");
  const [trackingSelectedPartner, setTrackingSelectedPartner] = useState("");
  const [trackingStats, setTrackingStats] = useState(null);
  const [trackingStatsLoading, setTrackingStatsLoading] = useState(false);
  const [trackingSelectedStatus, setTrackingSelectedStatus] = useState("");
  const [trackingExecuting, setTrackingExecuting] = useState(false);

  useEffect(() => {
    fetchShippingPartners();
    fetchSellers();
    fetchAvailableOperations();
    fetchHistory();
  }, []);

  // Poll for active operations
  useEffect(() => {
    if (!pollingOperationId) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/bulk-operations/history/${pollingOperationId}`);
        if (response.data.status === "completed" || response.data.status === "failed") {
          setPollingOperationId(null);
          fetchHistory();
        }
      } catch (error) {
        console.error("Error polling operation status:", error);
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [pollingOperationId]);

  // Fetch order count when filters change
  useEffect(() => {
    if (selectedPartner) {
      fetchOrderCount();
    } else {
      setOrderCount(null);
    }
  }, [selectedPartner, selectedSeller, selectedStatuses]);

  const fetchShippingPartners = async () => {
    try {
      const response = await axios.get(`${API}/bulk-operations/shipping-partners`);
      setShippingPartners(response.data);
    } catch (error) {
      toast.error("Failed to load shipping partners");
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/bulk-operations/sellers`);
      setSellers(response.data);
    } catch (error) {
      toast.error("Failed to load sellers");
    }
  };

  const fetchAvailableOperations = async () => {
    try {
      const response = await axios.get(`${API}/bulk-operations/available-operations`);
      setAvailableOperations(response.data);
    } catch (error) {
      toast.error("Failed to load operations");
    }
  };

  const fetchOrderCount = async () => {
    try {
      const params = new URLSearchParams();
      params.append("shipping_partner_id", selectedPartner);
      if (selectedSeller) params.append("seller_id", selectedSeller);
      if (selectedStatuses.length > 0) params.append("status_filter", selectedStatuses.join(","));
      
      const response = await axios.get(`${API}/bulk-operations/order-count?${params.toString()}`);
      setOrderCount(response.data.count);
    } catch (error) {
      setOrderCount(null);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(`${API}/bulk-operations/history?limit=50`);
      setHistory(response.data.history);
    } catch (error) {
      console.error("Failed to load history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Seller Charges Functions
  const fetchChargesStats = async (sellerId, shippingPartnerId) => {
    if (!sellerId || !shippingPartnerId) {
      setChargesStats(null);
      return;
    }
    
    setChargesStatsLoading(true);
    try {
      const response = await axios.get(`${API}/bulk-operations/seller-charges/stats/${sellerId}?shipping_partner_id=${shippingPartnerId}`);
      setChargesStats(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch seller charges statistics");
      setChargesStats(null);
    } finally {
      setChargesStatsLoading(false);
    }
  };

  const executeChargesCalculation = async () => {
    if (!chargesSelectedSeller) {
      toast.error("Please select a seller");
      return;
    }
    
    if (!chargesSelectedPartner) {
      toast.error("Please select a shipping partner");
      return;
    }
    
    if (!chargesStats) {
      toast.error("Please wait for statistics to load");
      return;
    }
    
    if (!chargesStats.has_commercial_structure) {
      toast.error("No active commercial structure found for this seller and shipping partner combination");
      return;
    }
    
    const ordersToProcess = recalculateAll ? chargesStats.total_eligible : chargesStats.not_calculated;
    if (ordersToProcess === 0) {
      toast.error("No orders to process");
      return;
    }
    
    setChargesExecuting(true);
    try {
      const payload = {
        seller_id: chargesSelectedSeller,
        shipping_partner_id: chargesSelectedPartner,
        recalculate_all: recalculateAll,
        adjustment_mode: recalculateAll ? adjustmentMode : "adjustment"
      };
      
      const response = await axios.post(`${API}/bulk-operations/seller-charges/execute`, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setPollingOperationId(response.data.operation_id);
        fetchHistory();
        
        // Reset form
        setChargesSelectedSeller("");
        setChargesSelectedPartner("");
        setChargesStats(null);
        setRecalculateAll(false);
        setAdjustmentMode("adjustment");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute calculation");
    } finally {
      setChargesExecuting(false);
    }
  };

  // Bulk Fetch Tracking Functions
  const fetchTrackingStats = async (sellerId, shippingPartnerId) => {
    if (!sellerId || !shippingPartnerId) {
      setTrackingStats(null);
      return;
    }
    
    setTrackingStatsLoading(true);
    try {
      const response = await axios.get(`${API}/bulk-operations/fetch-tracking/stats/${sellerId}/${shippingPartnerId}`);
      setTrackingStats(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch tracking statistics");
      setTrackingStats(null);
    } finally {
      setTrackingStatsLoading(false);
    }
  };

  const executeTrackingFetch = async () => {
    if (!trackingSelectedSeller) {
      toast.error("Please select a seller");
      return;
    }
    
    if (!trackingSelectedPartner) {
      toast.error("Please select a shipping partner");
      return;
    }
    
    if (!trackingSelectedStatus) {
      toast.error("Please select a status to fetch tracking for");
      return;
    }
    
    if (!trackingStats || !trackingStats.status_counts[trackingSelectedStatus]) {
      toast.error("No orders found for the selected status");
      return;
    }
    
    setTrackingExecuting(true);
    try {
      const payload = {
        seller_id: trackingSelectedSeller,
        shipping_partner_id: trackingSelectedPartner,
        status: trackingSelectedStatus
      };
      
      const response = await axios.post(`${API}/bulk-operations/fetch-tracking/execute`, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setPollingOperationId(response.data.operation_id);
        fetchHistory();
        
        // Reset form
        setTrackingSelectedSeller("");
        setTrackingSelectedPartner("");
        setTrackingStats(null);
        setTrackingSelectedStatus("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute tracking fetch");
    } finally {
      setTrackingExecuting(false);
    }
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const selectAllStatuses = () => {
    setSelectedStatuses(ORDER_STATUSES);
  };

  const clearAllStatuses = () => {
    setSelectedStatuses([]);
  };

  const executeOperation = async () => {
    if (!selectedPartner) {
      toast.error("Please select a shipping partner");
      return;
    }
    if (!selectedOperation) {
      toast.error("Please select an operation");
      return;
    }
    if (selectedOperation === "FETCH_SHIPPING_LABEL" && !selectedLabelSize) {
      toast.error("Please select a label size");
      return;
    }
    if (orderCount === 0) {
      toast.error("No orders match the selected criteria");
      return;
    }
    
    setExecuting(true);
    try {
      const payload = {
        operation_type: selectedOperation,
        shipping_partner_id: selectedPartner,
        seller_id: selectedSeller || null,
        status_filter: selectedStatuses,
        label_size: selectedLabelSize || null
      };
      
      const response = await axios.post(`${API}/bulk-operations/execute`, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setPollingOperationId(response.data.operation_id);
        fetchHistory();
        
        // Reset form
        setSelectedOperation("");
        setSelectedLabelSize("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute operation");
    } finally {
      setExecuting(false);
    }
  };

  const fetchOperationDetail = async (operationId) => {
    if (expandedHistory === operationId) {
      setExpandedHistory(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/bulk-operations/history/${operationId}`);
      // Store the detail in the history item
      setHistory(prev => prev.map(h => 
        h.id === operationId ? { ...h, detail: response.data } : h
      ));
      setExpandedHistory(operationId);
    } catch (error) {
      toast.error("Failed to load operation details");
    }
  };

  const getOperationIcon = (opType) => {
    const Icon = OPERATION_ICONS[opType] || RefreshCw;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bulk Operations</h1>
          <p className="text-slate-600">Execute bulk operations on orders</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab("general")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "general"
                ? "border-teal-500 text-teal-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              General Operations
            </div>
          </button>
          <button
            onClick={() => setActiveTab("fetch-tracking")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "fetch-tracking"
                ? "border-teal-500 text-teal-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Bulk Fetch Tracking
            </div>
          </button>
          <button
            onClick={() => setActiveTab("seller-charges")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "seller-charges"
                ? "border-teal-500 text-teal-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Calculate Seller Charges
            </div>
          </button>
        </div>

        {/* General Operations Tab */}
        {activeTab === "general" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filters & Execute Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Filter Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Filters</CardTitle>
                <CardDescription>Select criteria for bulk operation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Shipping Partner */}
                <div className="space-y-2">
                  <Label>Shipping Partner *</Label>
                  <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select shipping partner" />
                    </SelectTrigger>
                    <SelectContent>
                      {shippingPartners.map(partner => (
                        <SelectItem key={partner.id} value={partner.id}>
                          {partner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Seller */}
                <div className="space-y-2">
                  <Label>Seller (Optional)</Label>
                  <Select value={selectedSeller} onValueChange={(val) => setSelectedSeller(val === "_all" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Sellers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sellers</SelectItem>
                      {sellers.map(seller => (
                        <SelectItem key={seller.seller_id} value={seller.seller_id}>
                          {seller.brand_name || seller.company_registered_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Order Status Multi-select */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Order Status Filter</Label>
                    <div className="flex gap-2">
                      <button 
                        onClick={selectAllStatuses}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        All
                      </button>
                      <button 
                        onClick={clearAllStatuses}
                        className="text-xs text-slate-500 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-md max-h-48 overflow-y-auto">
                    {ORDER_STATUSES.map(status => (
                      <Badge
                        key={status}
                        variant={selectedStatuses.includes(status) ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${
                          selectedStatuses.includes(status) 
                            ? "bg-[#00A699] hover:bg-[#008b7f]" 
                            : "hover:bg-slate-100"
                        }`}
                        onClick={() => handleStatusToggle(status)}
                      >
                        {status.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    {selectedStatuses.length === 0 
                      ? "No filter - all statuses included" 
                      : `${selectedStatuses.length} status(es) selected`}
                  </p>
                </div>

                {/* Order Count */}
                {selectedPartner && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Matching Orders:</span>
                      <span className="text-lg font-semibold text-slate-900">
                        {orderCount !== null ? orderCount.toLocaleString() : "..."}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Operations Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Operation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {availableOperations.map(op => (
                  <div 
                    key={op.id}
                    onClick={() => setSelectedOperation(op.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedOperation === op.id 
                        ? "border-[#00A699] bg-[#00A699]/5" 
                        : "hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        selectedOperation === op.id ? "bg-[#00A699] text-white" : "bg-slate-100"
                      }`}>
                        {getOperationIcon(op.id)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{op.name}</p>
                        <p className="text-xs text-slate-500">{op.description}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Label Size (for shipping label operation) */}
                {selectedOperation === "FETCH_SHIPPING_LABEL" && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label>Label Size *</Label>
                    <Select value={selectedLabelSize} onValueChange={setSelectedLabelSize}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select label size" />
                      </SelectTrigger>
                      <SelectContent>
                        {LABEL_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value}>
                            {size.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Warning for terminal statuses */}
                {selectedOperation === "UPDATE_STATUS" && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <p className="text-sm text-yellow-700">
                        Orders with terminal statuses (DELIVERED, RETURN_DELIVERED, LOST, CANCELLED) will be skipped.
                      </p>
                    </div>
                  </div>
                )}

                {/* Execute Button */}
                <Button
                  onClick={executeOperation}
                  disabled={executing || !selectedPartner || !selectedOperation || orderCount === 0}
                  className="w-full"
                  style={{ backgroundColor: executing ? "#9CA3AF" : "#00A699" }}
                >
                  {executing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Execute Operation
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* History Panel */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Operation History
                    </CardTitle>
                    <CardDescription>Recent bulk operations and their results</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchHistory}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No operations executed yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {history.map(op => (
                      <div 
                        key={op.id}
                        className="border rounded-lg overflow-hidden"
                      >
                        {/* Header */}
                        <div 
                          className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => fetchOperationDetail(op.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-slate-100 rounded-lg">
                                {getOperationIcon(op.operation_type)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {availableOperations.find(o => o.id === op.operation_type)?.name || op.operation_type}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {op.shipping_partner_name} • {op.seller_name || "All Sellers"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={STATUS_COLORS[op.status]}>
                                {op.status === "processing" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                                {op.status}
                              </Badge>
                              {expandedHistory === op.id ? (
                                <ChevronUp className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                          
                          {/* Stats Summary */}
                          <div className="mt-3 flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1 text-slate-600">
                              <Clock className="w-3 h-3" />
                              {formatDateIST(op.started_at)}
                            </span>
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              {op.successful_orders} success
                            </span>
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="w-3 h-3" />
                              {op.failed_orders} failed
                            </span>
                            <span className="flex items-center gap-1 text-yellow-600">
                              <SkipForward className="w-3 h-3" />
                              {op.skipped_orders} skipped
                            </span>
                            <span className="text-slate-500">
                              Total: {op.total_orders}
                            </span>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedHistory === op.id && op.detail && (
                          <div className="border-t bg-slate-50 p-4">
                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                              <div>
                                <span className="text-slate-500">Initiated by:</span>
                                <span className="ml-2 font-medium">{op.detail.initiated_by_name}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Status Filter:</span>
                                <span className="ml-2 font-medium">
                                  {op.detail.status_filter?.length > 0 
                                    ? op.detail.status_filter.join(", ")
                                    : "All"}
                                </span>
                              </div>
                              {op.detail.label_size && (
                                <div>
                                  <span className="text-slate-500">Label Size:</span>
                                  <span className="ml-2 font-medium">{op.detail.label_size}</span>
                                </div>
                              )}
                              {op.detail.completed_at && (
                                <div>
                                  <span className="text-slate-500">Completed:</span>
                                  <span className="ml-2 font-medium">{formatDateIST(op.detail.completed_at)}</span>
                                </div>
                              )}
                            </div>

                            {/* Error Summary */}
                            {op.detail.error_summary?.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-32 overflow-y-auto">
                                  {op.detail.error_summary.map((err, i) => (
                                    <p key={i} className="text-xs text-red-600">{err}</p>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Sample Results */}
                            {op.detail.results?.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-slate-700 mb-2">
                                  Sample Results (last {Math.min(op.detail.results.length, 10)}):
                                </p>
                                <div className="bg-white border rounded max-h-48 overflow-y-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-100 sticky top-0">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Order</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LRN</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Message</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {op.detail.results.slice(-10).map((r, i) => (
                                        <tr key={i} className="border-t">
                                          <td className="px-4 py-3 align-middle">{r.order_number}</td>
                                          <td className="px-4 py-3 align-middle">{r.lrn}</td>
                                          <td className="px-4 py-3 align-middle">
                                            <Badge
                                              variant="outline"
                                              className={
                                                r.status === "success" ? "text-green-600 border-green-300" :
                                                r.status === "skipped" ? "text-yellow-600 border-yellow-300" :
                                                "text-red-600 border-red-300"
                                              }
                                            >
                                              {r.status}
                                            </Badge>
                                          </td>
                                          <td className="px-4 py-3 align-middle max-w-xs truncate">{r.message}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}

        {/* Bulk Fetch Tracking Tab */}
        {activeTab === "fetch-tracking" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Configuration Panel */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5" style={{color: '#00A699'}} />
                    Bulk Fetch Tracking
                  </CardTitle>
                  <CardDescription>
                    Fetch tracking updates from Delhivery for multiple orders
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Select Seller */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 1</span>
                      Select Seller
                    </Label>
                    <Select 
                      value={trackingSelectedSeller} 
                      onValueChange={(value) => {
                        setTrackingSelectedSeller(value);
                        setTrackingSelectedPartner("");
                        setTrackingStats(null);
                        setTrackingSelectedStatus("");
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a seller" />
                      </SelectTrigger>
                      <SelectContent>
                        {sellers.map(seller => (
                          <SelectItem key={seller.seller_id} value={seller.seller_id}>
                            {seller.brand_name || seller.company_registered_name} ({seller.seller_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Select Shipping Partner */}
                  {trackingSelectedSeller && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 2</span>
                        Select Shipping Partner
                      </Label>
                      <Select 
                        value={trackingSelectedPartner} 
                        onValueChange={(value) => {
                          setTrackingSelectedPartner(value);
                          setTrackingStats(null);
                          setTrackingSelectedStatus("");
                          if (value && trackingSelectedSeller) {
                            fetchTrackingStats(trackingSelectedSeller, value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a shipping partner" />
                        </SelectTrigger>
                        <SelectContent>
                          {shippingPartners.filter(p => p.name === "Delhivery").map(partner => (
                            <SelectItem key={partner.id} value={partner.id}>
                              {partner.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Only Delhivery orders can have tracking fetched automatically
                      </p>
                    </div>
                  )}

                  {/* Step 3: View Stats & Select Status */}
                  {trackingSelectedSeller && trackingSelectedPartner && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 3</span>
                        Order Statistics by Status
                      </Label>
                      
                      {trackingStatsLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      ) : trackingStats ? (
                        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                          <div className="text-sm text-slate-600">
                            <strong>{trackingStats.seller_name}</strong> → <strong>{trackingStats.shipping_partner_name}</strong>
                          </div>
                          
                          <div className="text-lg font-bold text-slate-900">
                            Total Orders with LRN: {trackingStats.total_with_lrn}
                          </div>
                          
                          {/* Status Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs text-slate-600">Select status to fetch tracking:</Label>
                            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {Object.entries(trackingStats.status_counts || {}).map(([status, count]) => (
                                <button
                                  key={status}
                                  onClick={() => setTrackingSelectedStatus(status)}
                                  className={`text-left p-3 rounded border text-sm transition-colors ${
                                    trackingSelectedStatus === status
                                      ? "border-teal-500 bg-teal-50 text-teal-800"
                                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-100"
                                  }`}
                                >
                                  <div className="font-medium">{status.replace(/_/g, ' ')}</div>
                                  <div className="text-xs text-slate-500">{count} orders</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {trackingSelectedStatus && (
                            <div className="bg-teal-50 border border-teal-200 rounded p-3 text-sm text-teal-800">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              Ready to fetch tracking for <strong>{trackingStats.status_counts[trackingSelectedStatus]}</strong> orders with status <strong>{trackingSelectedStatus.replace(/_/g, ' ')}</strong>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-slate-500">
                          <p className="text-sm">Select seller and shipping partner to view statistics</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Execute Button */}
                  <Button
                    onClick={executeTrackingFetch}
                    disabled={trackingExecuting || !trackingSelectedSeller || !trackingSelectedPartner || !trackingSelectedStatus}
                    className="w-full"
                    style={{ backgroundColor: trackingExecuting ? "#9CA3AF" : "#00A699" }}
                  >
                    {trackingExecuting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Fetching Tracking...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Fetch Tracking for {trackingSelectedStatus ? `${trackingStats?.status_counts[trackingSelectedStatus] || 0} Orders` : "Selected Status"}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* History Panel */}
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <History className="w-5 h-5" />
                        Operation History
                      </CardTitle>
                      <CardDescription>Recent bulk fetch tracking operations</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchHistory}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : history.filter(op => op.operation_type === "BULK_FETCH_TRACKING").length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No bulk fetch tracking operations executed yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {history.filter(op => op.operation_type === "BULK_FETCH_TRACKING").map(op => (
                        <div 
                          key={op.id}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-slate-100 rounded-lg">
                                <RefreshCw className="w-4 h-4 text-teal-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">Bulk Fetch Tracking</p>
                                <p className="text-xs text-slate-500">
                                  {op.shipping_partner_name} • {op.seller_name || "All Sellers"} • {op.params?.status}
                                </p>
                              </div>
                            </div>
                            <Badge className={STATUS_COLORS[op.status]}>
                              {op.status === "processing" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                              {op.status}
                            </Badge>
                          </div>
                          
                          <div className="mt-3 flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1 text-slate-600">
                              <Clock className="w-3 h-3" />
                              {formatDateIST(op.started_at)}
                            </span>
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              {op.successful_orders} success
                            </span>
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="w-3 h-3" />
                              {op.failed_orders} failed
                            </span>
                            <span className="flex items-center gap-1 text-yellow-600">
                              <SkipForward className="w-3 h-3" />
                              {op.skipped_orders} skipped
                            </span>
                            <span className="text-slate-500">
                              Total: {op.total_orders}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Seller Charges Calculation Tab */}
        {activeTab === "seller-charges" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Configuration Panel */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" style={{color: '#00A699'}} />
                    Calculate Seller Charges
                  </CardTitle>
                  <CardDescription>
                    Calculate &quot;Amount Charged to Seller&quot; for delivered orders
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Select Seller */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 1</span>
                      Select Seller
                    </Label>
                    <Select 
                      value={chargesSelectedSeller} 
                      onValueChange={(value) => {
                        setChargesSelectedSeller(value);
                        setChargesSelectedPartner("");
                        setChargesStats(null);
                        setRecalculateAll(false);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a seller" />
                      </SelectTrigger>
                      <SelectContent>
                        {sellers.map(seller => (
                          <SelectItem key={seller.seller_id} value={seller.seller_id}>
                            {seller.brand_name || seller.company_registered_name} ({seller.seller_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Select Shipping Partner */}
                  {chargesSelectedSeller && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 2</span>
                        Select Shipping Partner
                      </Label>
                      <Select 
                        value={chargesSelectedPartner} 
                        onValueChange={(value) => {
                          setChargesSelectedPartner(value);
                          setChargesStats(null);
                          setRecalculateAll(false);
                          if (value && chargesSelectedSeller) {
                            fetchChargesStats(chargesSelectedSeller, value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a shipping partner" />
                        </SelectTrigger>
                        <SelectContent>
                          {shippingPartners.map(partner => (
                            <SelectItem key={partner.id} value={partner.id}>
                              {partner.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Commercial structure is configured per seller + shipping partner combination
                      </p>
                    </div>
                  )}

                  {/* Step 3: View Stats */}
                  {chargesSelectedSeller && chargesSelectedPartner && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 3</span>
                        Order Statistics
                      </Label>
                      
                      {chargesStatsLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      ) : chargesStats ? (
                        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                          <div className="text-sm text-slate-600">
                            <strong>{chargesStats.seller_name}</strong> → <strong>{chargesStats.shipping_partner_name}</strong>
                          </div>
                          
                          {/* Commercial Structure Warning */}
                          {!chargesStats.has_commercial_structure && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                              <AlertTriangle className="w-4 h-4 inline mr-1" />
                              No active commercial structure found for this combination. 
                              <a 
                                href={`/admin/sellers/${chargesSelectedSeller}/commercial-structure`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline ml-1"
                              >
                                Configure now
                              </a>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded p-4 border">
                              <p className="text-xs text-slate-500">Total Eligible</p>
                              <p className="text-xl font-bold text-slate-800">{chargesStats.total_eligible}</p>
                              <p className="text-xs text-slate-400">Delivered / Return Delivered</p>
                            </div>
                            <div className="bg-white rounded p-4 border">
                              <p className="text-xs text-slate-500">With Freight Data</p>
                              <p className="text-xl font-bold text-blue-600">{chargesStats.with_freight_charges}</p>
                              <p className="text-xs text-slate-400">Ready for calculation</p>
                            </div>
                            <div className="bg-white rounded p-4 border border-green-200">
                              <p className="text-xs text-green-600">Already Calculated</p>
                              <p className="text-xl font-bold text-green-600">{chargesStats.already_calculated}</p>
                            </div>
                            <div className="bg-white rounded p-4 border border-orange-200">
                              <p className="text-xs text-orange-600">Not Calculated</p>
                              <p className="text-xl font-bold text-orange-600">{chargesStats.not_calculated}</p>
                            </div>
                          </div>
                          {chargesStats.missing_freight_charges > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                              <AlertTriangle className="w-4 h-4 inline mr-1" />
                              {chargesStats.missing_freight_charges} orders are missing freight charges data. 
                              System will attempt to fetch from Delhivery during calculation.
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Step 4: Choose Calculation Option */}
                  {chargesStats && chargesStats.total_eligible > 0 && chargesStats.has_commercial_structure && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded">Step 4</span>
                        Calculation Option
                      </Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50">
                          <input
                            type="radio"
                            name="calcOption"
                            checked={!recalculateAll}
                            onChange={() => setRecalculateAll(false)}
                            className="w-4 h-4 text-teal-600"
                          />
                          <div>
                            <p className="font-medium text-sm">Only Not Calculated</p>
                            <p className="text-xs text-slate-500">
                              Process {chargesStats.not_calculated} orders that don&apos;t have charges calculated yet
                            </p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50">
                          <input
                            type="radio"
                            name="calcOption"
                            checked={recalculateAll}
                            onChange={() => setRecalculateAll(true)}
                            className="w-4 h-4 text-teal-600"
                          />
                          <div>
                            <p className="font-medium text-sm">Recalculate All</p>
                            <p className="text-xs text-slate-500">
                              Process all {chargesStats.total_eligible} orders and adjust wallet transactions if amounts differ
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Step 5: Adjustment Mode (only shown when Recalculate All is selected) */}
                  {chargesStats && chargesStats.total_eligible > 0 && chargesStats.has_commercial_structure && recalculateAll && chargesStats.already_calculated > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-0.5 rounded">Step 5</span>
                        Wallet Transaction Adjustment Mode
                      </Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50">
                          <input
                            type="radio"
                            name="adjustmentMode"
                            checked={adjustmentMode === "overwrite"}
                            onChange={() => setAdjustmentMode("overwrite")}
                            className="w-4 h-4 text-orange-600"
                          />
                          <div>
                            <p className="font-medium text-sm">Overwrite Existing Transaction</p>
                            <p className="text-xs text-slate-500">
                              Update the amount in the original transaction. Original transaction date will be preserved.
                            </p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50">
                          <input
                            type="radio"
                            name="adjustmentMode"
                            checked={adjustmentMode === "adjustment"}
                            onChange={() => setAdjustmentMode("adjustment")}
                            className="w-4 h-4 text-orange-600"
                          />
                          <div>
                            <p className="font-medium text-sm">Create Adjustment Entry</p>
                            <p className="text-xs text-slate-500">
                              Create a new credit/debit note for the difference. New entry will have current timestamp.
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Execute Button */}
                  {chargesStats && chargesStats.total_eligible > 0 && chargesStats.has_commercial_structure && (
                    <Button
                      onClick={executeChargesCalculation}
                      disabled={chargesExecuting || (recalculateAll ? chargesStats.total_eligible === 0 : chargesStats.not_calculated === 0)}
                      className="w-full"
                      style={{backgroundColor: '#00A699'}}
                    >
                      {chargesExecuting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Calculate Charges for {recalculateAll ? chargesStats.total_eligible : chargesStats.not_calculated} Orders
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* History Panel for Seller Charges */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <History className="w-5 h-5" />
                        Calculation History
                      </CardTitle>
                      <CardDescription>Recent seller charges calculations</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchHistory}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {history.filter(op => op.operation_type === "CALCULATE_SELLER_CHARGES").length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                          <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>No seller charges calculations yet</p>
                        </div>
                      ) : (
                        history.filter(op => op.operation_type === "CALCULATE_SELLER_CHARGES").map(op => (
                          <div 
                            key={op.id}
                            className="border rounded-lg overflow-hidden"
                          >
                            <div 
                              className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => fetchOperationDetail(op.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-teal-100 rounded-lg">
                                    <Calculator className="w-4 h-4 text-teal-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">
                                      Calculate Seller Charges
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {op.seller_name} • {op.recalculate_all ? "Recalculate All" : "Only Not Calculated"}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge className={STATUS_COLORS[op.status]}>
                                    {op.status === "processing" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                                    {op.status}
                                  </Badge>
                                  {expandedHistory === op.id ? (
                                    <ChevronUp className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  )}
                                </div>
                              </div>
                              
                              <div className="mt-3 flex items-center gap-4 text-xs">
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Clock className="w-3 h-3" />
                                  {formatDateIST(op.started_at)}
                                </span>
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-3 h-3" />
                                  {op.successful_orders} success
                                </span>
                                <span className="flex items-center gap-1 text-red-600">
                                  <XCircle className="w-3 h-3" />
                                  {op.failed_orders} failed
                                </span>
                                {op.skipped_orders > 0 && (
                                  <span className="flex items-center gap-1 text-yellow-600">
                                    <SkipForward className="w-3 h-3" />
                                    {op.skipped_orders} skipped
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Expanded Detail */}
                            {expandedHistory === op.id && op.detail && (
                              <div className="border-t bg-slate-50 p-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div className="bg-white p-4 rounded border">
                                    <p className="text-xs text-slate-500">Total Orders</p>
                                    <p className="text-lg font-semibold">{op.detail.total_orders}</p>
                                  </div>
                                  <div className="bg-white p-4 rounded border">
                                    <p className="text-xs text-green-600">Successful</p>
                                    <p className="text-lg font-semibold text-green-600">{op.detail.successful_orders}</p>
                                  </div>
                                  <div className="bg-white p-4 rounded border">
                                    <p className="text-xs text-red-600">Failed</p>
                                    <p className="text-lg font-semibold text-red-600">{op.detail.failed_orders}</p>
                                  </div>
                                  {op.detail.total_amount_calculated && (
                                    <div className="bg-white p-4 rounded border">
                                      <p className="text-xs text-teal-600">Total Calculated</p>
                                      <p className="text-lg font-semibold text-teal-600">₹{op.detail.total_amount_calculated?.toLocaleString()}</p>
                                    </div>
                                  )}
                                </div>

                                {op.detail.error_summary?.length > 0 && (
                                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                                    <p className="text-sm font-medium text-red-800 mb-2">Error Summary:</p>
                                    <ul className="text-xs text-red-700 space-y-1">
                                      {op.detail.error_summary.map((err, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                          {err}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BulkOperations;
